import {
  Observer,
  PointerInfo,
  Scene,
  Vector2,
  PointerEventTypes,
  GPUPicker,
  AbstractMesh,
} from '@babylonjs/core'
import { AdvancedDynamicTexture, Line, Rectangle } from '@babylonjs/gui'
import { isDoorMesh, isRoofMesh, isWallMesh, isWindowMesh } from '../utils'

export class SelectionArea {
  private readonly lineWidth: number = 2
  private readonly selectionAreaColor: string = '#1705E5'

  private scene: Scene
  private gpuPicker: GPUPicker
  private pickableMeshes: AbstractMesh[] = []

  private originalDefaultCursor: string
  private originalHoverCursor: string
  private selectionUI: AdvancedDynamicTexture =
    AdvancedDynamicTexture.CreateFullscreenUI('SelectionArea')

  private lineTop: Line | null = null
  private lineLeft: Line | null = null
  private lineRight: Line | null = null
  private lineBottom: Line | null = null
  private rectangle: Rectangle | null = null

  private onPointerObservable: Observer<PointerInfo>
  private topLeft: Vector2 = new Vector2()
  private bottomRight: Vector2 = new Vector2()
  private isDragging: boolean = false

  private get crosshairCursor(): string {
    return 'url(/images/icons/spacialCrosshairCursor.svg) 12 8, crosshair'
  }

  constructor(
    scene: Scene,
    onPickHandler: (
      eventType: PointerEventTypes,
      pickedMeshes: AbstractMesh[],
    ) => void,
  ) {
    this.scene = scene
    this.gpuPicker = new GPUPicker()
    this.updatePickingList()

    this.originalDefaultCursor = this.scene.defaultCursor
    this.scene.defaultCursor = this.crosshairCursor

    this.originalHoverCursor = this.scene.hoverCursor
    this.scene.hoverCursor = this.crosshairCursor

    const startPosition = new Vector2()
    this.onPointerObservable = scene.onPointerObservable.add(
      async (e) => {
        switch (e.type) {
          case PointerEventTypes.POINTERDOWN:
            this.isDragging = true
            startPosition.copyFromFloats(e.event.offsetX, e.event.offsetY)

            this.lineTop = new Line('top')
            this.lineLeft = new Line('left')
            this.lineRight = new Line('right')
            this.lineBottom = new Line('bottom')

            this.lineTop.color =
              this.lineLeft.color =
              this.lineRight.color =
              this.lineBottom.color =
                this.selectionAreaColor

            this.lineTop.lineWidth =
              this.lineLeft.lineWidth =
              this.lineRight.lineWidth =
              this.lineBottom.lineWidth =
                this.lineWidth

            this.rectangle = new Rectangle('rectangle')
            this.rectangle.background = this.rectangle.color =
              this.selectionAreaColor
            this.rectangle.width = this.rectangle.height = 0
            this.rectangle.thickness = 0
            this.rectangle.alpha = 0.05

            this.selectionUI
              .addControl(this.lineTop)
              .addControl(this.lineLeft)
              .addControl(this.lineRight)
              .addControl(this.lineBottom)
              .addControl(this.rectangle)
            break
          case PointerEventTypes.POINTERMOVE:
            if (!this.isDragging) {
              return
            }

            this.topLeft.copyFromFloats(
              Math.min(startPosition.x, e.event.offsetX),
              Math.min(startPosition.y, e.event.offsetY),
            )

            this.bottomRight.copyFromFloats(
              Math.max(startPosition.x, e.event.offsetX),
              Math.max(startPosition.y, e.event.offsetY),
            )

            this.updateSelectionAreaUI()

            onPickHandler(
              e.type,
              await this.pickMultiByRegionAsync(this.topLeft, this.bottomRight),
            )
            break
          case PointerEventTypes.POINTERUP:
            this.isDragging = false

            onPickHandler(
              e.type,
              await this.pickMultiByRegionAsync(this.topLeft, this.bottomRight),
            )

            this.selectionUI.rootContainer.clearControls()
            this.lineLeft?.dispose()
            this.lineTop?.dispose()
            this.lineRight?.dispose()
            this.lineBottom?.dispose()
            this.rectangle?.dispose()

            this.lineLeft =
              this.lineTop =
              this.lineRight =
              this.lineBottom =
              this.rectangle =
                null
            break
        }
      },
      PointerEventTypes.POINTERDOWN |
        PointerEventTypes.POINTERMOVE |
        PointerEventTypes.POINTERUP,
    )
  }

  updatePickingList() {
    this.pickableMeshes = this.scene.meshes.filter(
      // TODO: Update this to support more meshes
      // For instanced meshes take a look: https://doc.babylonjs.com/features/featuresDeepDive/mesh/interactions/picking_collisions#gpu-picking
      (m) =>
        m.isVisible &&
        (isWallMesh(m) || isRoofMesh(m) || isDoorMesh(m) || isWindowMesh(m)),
    )
    this.gpuPicker.setPickingList(this.pickableMeshes)
  }

  dispose() {
    this.scene.defaultCursor = this.originalDefaultCursor
    this.scene.hoverCursor = this.originalHoverCursor
    this.pickableMeshes = []
    this.gpuPicker.dispose()
    this.selectionUI.dispose()
    this.scene.onPointerObservable.remove(this.onPointerObservable)
  }

  private updateSelectionAreaUI() {
    const halfLineWidth = this.lineWidth / 2

    if (this.lineLeft) {
      this.lineLeft.x1 = Math.round(this.topLeft.x)
      this.lineLeft.y1 = Math.round(this.topLeft.y - halfLineWidth)
      this.lineLeft.x2 = Math.round(this.topLeft.x)
      this.lineLeft.y2 = Math.round(this.bottomRight.y + halfLineWidth)
    }

    if (this.lineTop) {
      this.lineTop.x1 = Math.round(this.topLeft.x - halfLineWidth)
      this.lineTop.y1 = Math.round(this.topLeft.y)
      this.lineTop.x2 = Math.round(this.bottomRight.x + halfLineWidth)
      this.lineTop.y2 = Math.round(this.topLeft.y)
    }

    if (this.lineRight) {
      this.lineRight.x1 = Math.round(this.bottomRight.x)
      this.lineRight.y1 = Math.round(this.topLeft.y - halfLineWidth)
      this.lineRight.x2 = Math.round(this.bottomRight.x)
      this.lineRight.y2 = Math.round(this.bottomRight.y + halfLineWidth)
    }

    if (this.lineBottom) {
      this.lineBottom.x1 = Math.round(this.topLeft.x - halfLineWidth)
      this.lineBottom.y1 = Math.round(this.bottomRight.y)
      this.lineBottom.x2 = Math.round(this.bottomRight.x + halfLineWidth)
      this.lineBottom.y2 = Math.round(this.bottomRight.y)
    }

    if (this.rectangle) {
      const width = Math.abs(this.bottomRight.x - this.topLeft.x)
      const height = Math.abs(this.bottomRight.y - this.topLeft.y)
      const canvasWidth = this.scene.getEngine().getRenderWidth()
      const canvasHeight = this.scene.getEngine().getRenderHeight()

      this.rectangle.width = `${width}px`
      this.rectangle.height = `${height}px`
      this.rectangle.left = Math.round(
        this.topLeft.x + (width - canvasWidth) / 2,
      )
      this.rectangle.top = Math.round(
        this.topLeft.y + (height - canvasHeight) / 2,
      )
    }
  }

  private async pickMultiByRegionAsync(topLeft: Vector2, bottomRight: Vector2) {
    const pickedMeshesId = new Set<number>()
    this.clearReadBuffer()

    await this.gpuPicker.multiPickAsync([topLeft, bottomRight])

    const w = bottomRight.x - topLeft.x
    const h = bottomRight.y - topLeft.y

    for (let i = 0; i < w * h - 1; i += 1) {
      const colorId = this.getColorIdFromReadBuffer(i * 4)
      if (colorId > 0 && !pickedMeshesId.has(colorId)) {
        pickedMeshesId.add(colorId)
      }
    }

    return Array.from(pickedMeshesId).map(
      (colorId) => this.pickableMeshes[colorId - 1],
    )
  }

  private clearReadBuffer(): void {
    ;(this.gpuPicker as any)._readbuffer?.fill(0)
  }

  private getColorIdFromReadBuffer(offset: number): number {
    return (this.gpuPicker as any)._getColorIdFromReadBuffer(offset)
  }
}
